import React, { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import BusinessCustomersActions from "../../../Redux/transition/customers/business-customer.reducer";
import {
    createCustomHandleApostropheChange,
    removeTrailingSpacesAndDashes,
} from "../../../Components/CleanInputs";
import CustomToast from "../../../Components/CustomToast";
import { getSegmentDisplayName, getSegmentId } from "./segmentUtils";

export const EditSegmentDialog = ({
    visible,
    onHide,
    businessId,
    segment,
    onSegmentsChanged,
    segmentFetchOptions,
}) => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    const [errors, setErrors] = useState({});
    const [err, setErr] = useState("");
    const [segmentName, setSegmentName] = useState("");
    const [toastTrigger, setToastTrigger] = useState(false);
    const [saveClicked, setSaveClicked] = useState(false);
    const [saveClickedError, setSaveClickedError] = useState(false);
    const [toastData, setToastData] = useState({
        summary: "",
        severity: "",
        detail: "",
        life: 0,
    });

    const { updatingSegment, updatedSegment, updateSegmentError } = useSelector(
        (state) => state.businessCustomers
    );

    const updateSegment = useCallback(
        (segmentId, payload) =>
            dispatch(BusinessCustomersActions.updateSegmentRequest(segmentId, payload)),
        [dispatch]
    );
    const getSegment = useCallback(
        (id) =>
            dispatch(BusinessCustomersActions.fetchingSegmentRequest(id, segmentFetchOptions)),
        [dispatch, segmentFetchOptions]
    );
    const getSegmentCount = useCallback(
        (id) => dispatch(BusinessCustomersActions.fetchingSegmentCountRequest(id)),
        [dispatch]
    );

    const showToast = (summary, severity, detail, life = 3000) => {
        setToastData({ summary, severity, detail, life });
        setToastTrigger(true);
    };

    const formik = useFormik({
        initialValues: { name: "" },
        enableReinitialize: false,
        validate: (data) => {
            const validationErrors = {};
            if (!data.name?.trim()) {
                validationErrors.name = t("products.validate_error");
            }
            return validationErrors;
        },
        onSubmit: (data) => {
            const segmentId = getSegmentId(segment);
            if (segmentId == null) {
                return;
            }
            const payload = {
                id: segmentId,
                name: removeTrailingSpacesAndDashes(data.name) || null,
                business: { id: businessId },
            };
            setSegmentName(data.name);
            setSaveClicked(true);
            updateSegment(segmentId, payload);
        },
    });

    useEffect(() => {
        if (!visible || !segment) {
            return;
        }
        const name = getSegmentDisplayName(segment);
        formik.setFieldValue("name", name);
        formik.setFieldTouched("name", false);
        setErr("");
        setSaveClicked(false);
        setSaveClickedError(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, segment?.id]);

    useEffect(() => {
        if (
            !updatingSegment &&
            updateSegmentError?.status === 400 &&
            updateSegmentError?.detail &&
            saveClickedError
        ) {
            setErr(updateSegmentError.detail.split('"')[1]);
        } else if (!updatingSegment && updateSegmentError && saveClickedError) {
            setErr(t("segment.failure_edit"));
        } else {
            setErr("");
        }
    }, [updateSegmentError, updatingSegment, saveClickedError, t]);

    useEffect(() => {
        if (!updatingSegment && updatedSegment?.id && saveClicked) {
            showToast(t("success"), "success", t("segment.success_edit"));
        }
        if (!updatingSegment && updateSegmentError && saveClicked) {
            showToast(t("err"), "error", t("segment.failure_edit"), 5000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatingSegment, updatedSegment, updateSegmentError, saveClicked]);

    useEffect(() => {
        if (formik.values.name) {
            setSaveClickedError(false);
        }
    }, [formik.values.name]);

    const commonParams = [formik, setErrors, t];
    const customHandleChange = createCustomHandleApostropheChange(...commonParams);

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) =>
        isFormFieldValid(name) &&
        saveClicked && <small className="p-error">{formik.errors[name]}</small>;

    const initialName = getSegmentDisplayName(segment);
    const nameUnchanged =
        removeTrailingSpacesAndDashes(formik.values.name) ===
        removeTrailingSpacesAndDashes(initialName);

    return (
        <div>
            <CustomToast
                {...toastData}
                trigger={toastTrigger}
                handleActionsAfterToast={() => {
                    getSegment(businessId);
                    getSegmentCount(businessId);
                    onSegmentsChanged?.();
                    onHide();
                    setToastTrigger(false);
                    setSaveClicked(false);
                    setSaveClickedError(false);
                    setErr("");
                    setSegmentName("");
                    formik.resetForm();
                }}
                handleActionsAfterErrorToast={() => {
                    setToastTrigger(false);
                    setSaveClicked(false);
                    setSaveClickedError(true);
                }}
            />
            <Dialog
                visible={visible}
                onHide={() => {
                    onHide();
                    formik.resetForm();
                    setErr("");
                    setSaveClicked(false);
                    setSaveClickedError(false);
                }}
                header={t("segment.edit_segment")}
                style={{ width: "350px" }}
            >
                <form onSubmit={formik.handleSubmit} className="p-fluid" style={{ paddingInline: "4px" }}>
                    <div className="p-field p-mb-3 mt-4">
                        <label htmlFor="edit_segment_name" className={classNames("product-form-label")}>
                            {t("common.name")}
                        </label>
                        <InputText
                            id="edit_segment_name"
                            name="name"
                            value={formik.values.name}
                            onChange={customHandleChange}
                            className="segment-edit-name-input"
                            style={{ width: "100%", height: "3rem" }}
                            maxLength={100}
                        />
                        {getFormErrorMessage("name")}
                        {err && (
                            <Trans
                                i18nKey="segment.message_group_name_exists"
                                values={{ segment: segmentName }}
                                components={{
                                    1: <span className="tags_error_message" />,
                                    2: <span className="tags_error_message_bold" />,
                                    3: <span className="tags_error_message" />,
                                }}
                                className="mt-1"
                            />
                        )}
                        {errors.name ? (
                            <p className="character_errors">{errors.name}</p>
                        ) : (
                            <p className="segment_hint">{t("segment.segment_field_hint")}</p>
                        )}
                    </div>

                    <div className="p-field p-mb-3 mt-4">
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                type="submit"
                                style={{ width: "auto", backgroundColor: "#d0021b" }}
                                label={t("products.save_product")}
                                loading={updatingSegment}
                                disabled={
                                    saveClicked ||
                                    !formik.values.name?.trim() ||
                                    saveClickedError ||
                                    nameUnchanged
                                }
                                className="p-mt-2"
                            />
                        </div>
                    </div>
                </form>
            </Dialog>
        </div>
    );
};
